import { useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useAtom } from 'jotai';
import { currentUserAtom, userRolesAtom } from '@src/atoms/projectScope';
import { projectScope } from '@src/atoms/projectScope';

/**
 * ProjectSourceClerk - Bridges Clerk authentication to Rowy's atom-based state
 *
 * This component:
 * 1. Monitors Clerk's authentication state via useUser()
 * 2. Updates currentUserAtom when user signs in/out
 * 3. Extracts roles from Clerk's publicMetadata and updates userRolesAtom
 *
 * Replaces ProjectSourceFirebase for Clerk-based authentication.
 */
export default function ProjectSourceClerk() {
  const { user, isLoaded, isSignedIn } = useUser();
  const [, setCurrentUser] = useAtom(currentUserAtom, projectScope);
  const [, setUserRoles] = useAtom(userRolesAtom, projectScope);

  // CRITICAL DEBUG: Log component mount/unmount
  useEffect(() => {
    console.log('🚀 [ProjectSourceClerk] Component mounted');
    console.log('🔍 [ProjectSourceClerk] Clerk SDK available:', typeof window !== 'undefined' && !!(window as any).Clerk);
    console.log('🔍 [ProjectSourceClerk] useUser hook initialized');

    return () => {
      console.log('💀 [ProjectSourceClerk] Component unmounted');
    };
  }, []);

  // CRITICAL DEBUG: Log every auth state change
  useEffect(() => {
    console.log('📊 [ProjectSourceClerk] Auth state changed:', {
      isLoaded,
      isSignedIn,
      hasUser: !!user,
      userId: user?.id || 'none',
      timestamp: new Date().toISOString()
    });

    // Wait for Clerk to finish loading
    if (!isLoaded) {
      console.log('⏳ [ProjectSourceClerk] Waiting for Clerk to load...');
      return;
    }

    if (isSignedIn && user) {
      // User is authenticated - set currentUserAtom
      const userData = {
        uid: user.id,
        email: user.primaryEmailAddress?.emailAddress || null,
        emailVerified: user.primaryEmailAddress?.verification.status === 'verified',
        displayName: user.fullName || user.firstName || null,
        photoURL: user.imageUrl || null,
        isAnonymous: false,
      };

      setCurrentUser(userData);

      // Extract roles from Clerk publicMetadata
      const metadata = user.publicMetadata as { roles?: string[] };
      setUserRoles(metadata.roles || []);

      console.log('✅ [ProjectSourceClerk] User authenticated successfully!');
      console.log('👤 [ProjectSourceClerk] User data:', userData);
      console.log('🔐 [ProjectSourceClerk] Roles:', metadata.roles || []);
      console.log('📧 [ProjectSourceClerk] Email verified:', userData.emailVerified);
    } else {
      // User is not signed in - clear state
      setCurrentUser(null);
      setUserRoles([]);

      console.log('❌ [ProjectSourceClerk] User not authenticated');
      console.log('🔓 [ProjectSourceClerk] currentUserAtom set to null');
      console.log('🔓 [ProjectSourceClerk] userRolesAtom set to []');
    }
  }, [isLoaded, isSignedIn, user, setCurrentUser, setUserRoles]);

  // This component has no UI - it only manages auth state
  return null;
}
